// Inline SVG icons.
//
// Upstream renders glyphs from @mattermost/compass-icons as an icon font
// (`<i className="icon icon-arrow-down">`). A font is a heavy, silently
// degrading dependency for a clone, so every icon here is an inline path
// drawn to match the same 24px grid. Shapes are approximations, not the
// upstream artwork — see README "视觉近似".

import {ReactElement, SVGProps} from 'react'

export type IconName =
    | 'chevron-down' | 'chevron-right' | 'chevron-left' | 'chevron-up'
    | 'close' | 'search' | 'add' | 'dots' | 'dots-vertical'
    | 'check' | 'trash' | 'filter' | 'sort-asc' | 'sort-desc'
    | 'eye' | 'eye-off' | 'settings' | 'logout' | 'user' | 'users'
    | 'view-board' | 'view-table' | 'view-gallery' | 'view-calendar'
    | 'title' | 'text' | 'number' | 'date' | 'checkbox' | 'color'
    | 'link' | 'message' | 'attachment' | 'image' | 'divider' | 'drag'
    | 'grip' | 'star' | 'star-outline' | 'help' | 'share' | 'duplicate'
    | 'edit' | 'menu-open' | 'menu-close' | 'warning' | 'loading' | 'globe'

const paths: Record<IconName, ReactElement> = {
    'chevron-down': <path d='M7 10l5 5 5-5z'/>,
    'chevron-up': <path d='M7 14l5-5 5 5z'/>,
    'chevron-right': <path d='M10 7l5 5-5 5z'/>,
    'chevron-left': <path d='M14 7l-5 5 5 5z'/>,
    close: <path d='M18.3 7.11L16.89 5.7 12 10.59 7.11 5.7 5.7 7.11 10.59 12 5.7 16.89l1.41 1.41L12 13.41l4.89 4.89 1.41-1.41L13.41 12z'/>,
    search: <path d='M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zM9.5 14A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z'/>,
    add: <path d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z'/>,
    dots: <path d='M6 10a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4z'/>,
    'dots-vertical': <path d='M12 8a2 2 0 100-4 2 2 0 000 4zm0 2a2 2 0 100 4 2 2 0 000-4zm0 6a2 2 0 100 4 2 2 0 000-4z'/>,
    check: <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/>,
    trash: <path d='M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z'/>,
    filter: <path d='M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z'/>,
    'sort-asc': <path d='M19 7h-9V5h9zm-9 10h9v-2h-9zM5 5v14l-2-1.5 0-11zm0 0l2 1.5v11L5 19z'/>,
    'sort-desc': <path d='M19 7h-9V5h9zm-9 10h9v-2h-9zM5 5l2 1.5v11L5 19z'/>,
    eye: <path d='M12 6a7 7 0 00-7 7 7 7 0 007-7 7 7 0 007 7 7 7 0 00-7-7zm0 12a5 5 0 01-5-5 5 5 0 0110 0zm0-9a4 4 0 104 4 4 4 0 00-4-4z'/>,
    'eye-off': <path d='M12 6a7 7 0 017 7 7 7 0 01-7 7 7 7 0 01-7-7 7 7 0 017-7zm0 10a3 3 0 10-3-3 3 3 0 003 3z'/>,
    settings: <path d='M19.4 13a7.8 7.8 0 000-2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 00-1.7-1L15 3H9.9l-.4 2.6a7.6 7.6 0 00-1.7 1l-2.4-1-2 3.4L2.5 11a7.8 7.8 0 000 2l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 001.7 1l.4 2.6H15l.4-2.6a7.6 7.6 0 001.7-1l2.4 1 2-3.4zM12 15.5A3.5 3.5 0 1115.5 12 3.5 3.5 0 0112 15.5z'/>,
    logout: <path d='M17 7l-1.4 1.4L18.2 11H9v2h9.2l-2.6 2.6L17 17l5-5zM4 5h8V3H4a2 2 0 00-2 2v14a2 2 0 002 2h8v-2H4z'/>,
    user: <path d='M12 12a4 4 0 10-4-4 4 4 0 004 4zm0 2c-3.3 0-8 1.7-8 5v3h16v-3c0-3.3-4.7-5-8-5z'/>,
    users: <path d='M16 11a3 3 0 10-3-3 3 3 0 003 3zm-8 0a3 3 0 10-3-3 3 3 0 003 3zm0 2c-2.3 0-7 1.2-7 3.5V19h14v-2.5C15 14.2 10.3 13 8 13zm8 0c-.3 0-.7 0-1.1.1a5.4 5.4 0 013.1 4.9V19h5v-2.5c0-2.3-4.7-3.5-7-3.5z'/>,
    'view-board': <path d='M4 4h5v14H4zm6 0h5v9h-5zm6 0h4v12h-4z'/>,
    'view-table': <path d='M3 4h18v4H3zm0 6h18v3H3zm0 5h18v3H3z'/>,
    'view-gallery': <path d='M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z'/>,
    'view-calendar': <path d='M7 2v2H5a2 2 0 00-2 2v13a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2V2h-2v2H9V2zM5 9h14v10H5z'/>,
    title: <path d='M5 4h14v3h-2V6h-4v12h2v2H9v-2h2V6H7v1H5z'/>,
    text: <path d='M4 6h16v2H4zm0 5h16v2H4zm0 5h11v2H4z'/>,
    number: <path d='M6 20l2.5-7H6v-2h3.2L11 4h2L12.2 11H17v2h-3.3L11.5 20h-2l1.6-7H7.6L6 20z'/>,
    date: <path d='M7 2v2H5a2 2 0 00-2 2v13a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2V2h-2v2H9V2zm8 9H9v2h6z'/>,
    checkbox: <path d='M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-9 12l-4-4 1.4-1.4L10 12.2l6.2-6.2L17.6 7.4z'/>,
    color: <path d='M12 3a9 9 0 000 18 2 2 0 002-2 2 2 0 00-.5-1.3 2 2 0 011.5-2h2A4 4 0 0021 12 9 9 0 0012 3zm-4 9a1.5 1.5 0 111.5-1.5A1.5 1.5 0 018 12zm2.5-4A1.5 1.5 0 1112 6.5 1.5 1.5 0 0110.5 8zm4 0A1.5 1.5 0 1116 6.5 1.5 1.5 0 0114.5 8z'/>,
    link: <path d='M3.9 12a3.1 3.1 0 013.1-3.1h4V7H7a5 5 0 000 10h4v-1.9H7A3.1 3.1 0 013.9 12zM8 13h8v-2H8zm9-6h-4v1.9h4a3.1 3.1 0 010 6.2h-4V17h4a5 5 0 000-10z'/>,
    message: <path d='M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2z'/>,
    attachment: <path d='M16.5 6.5v8.8a4.5 4.5 0 01-9 0V5a3 3 0 016 0v9.5a1.5 1.5 0 01-3 0V6.5H9v8a3 3 0 003 3 3 3 0 003-3v-9a5 5 0 00-10 0v10.3a6 6 0 0012 0v-8.8z'/>,
    image: <path d='M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5z'/>,
    divider: <path d='M3 11h18v2H3z'/>,
    drag: <path d='M9 4a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9-16a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z'/>,
    grip: <path d='M3 11h18v2H3z'/>,
    star: <path d='M12 17.3l-6.2 3.7 1.6-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.4 4.8 1.6 7z'/>,
    'star-outline': <path d='M22 9.2l-7.2-.6L12 2 9.2 8.6 2 9.2l5.4 4.8-1.6 7L12 17.3l6.2 3.7-1.6-7zM12 15l-2.6 1.6.7-3L7.8 11l3-.3L12 8l1.2 2.8 3 .3-2.3 2.5.7 3z'/>,
    help: <path d='M12 2a10 10 0 1010 10A10 10 0 0012 2zm1 17h-2v-2h2zm2.1-7.7l-.9.9A3.6 3.6 0 0013 14h-2v-.5a4 4 0 011.2-2.8l1.2-1.3A2 2 0 0013.6 8a2 2 0 00-2-2 2 2 0 00-2 2H7.6a4 4 0 014-4 4 4 0 014 4 3.6 3.6 0 01-.5 1.3z'/>,
    share: <path d='M18 16.1a3 3 0 00-2 .8l-7.1-4.2a3 3 0 000-.9l7-4.1A3 3 0 1015 5a3 3 0 000 .9L7.9 10a3 3 0 100 4l7.1 4.2A3 3 0 0015 19a3 3 0 103-2.9z'/>,
    duplicate: <path d='M16 1H4a2 2 0 00-2 2v14h2V3h12zM19 5H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11z'/>,
    edit: <path d='M3 17.2V21h3.8L17.9 9.9l-3.8-3.8zM20.7 7.1a1 1 0 000-1.4l-2.4-2.4a1 1 0 00-1.4 0l-1.8 1.8 3.8 3.8z'/>,
    'menu-open': <path d='M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z'/>,
    'menu-close': <path d='M3 6h18v2H3zm0 10h18v2H3z'/>,
    warning: <path d='M1 21h22L12 2zm12-3h-2v-2h2zm0-4h-2v-4h2z'/>,
    loading: <path d='M12 4V2A10 10 0 002 12h2a8 8 0 018-8z'/>,
    globe: <path d='M12 2a10 10 0 1010 10A10 10 0 0012 2zm6.9 6h-2.5a15 15 0 00-1.3-3.4A8 8 0 0118.9 8zM12 4a13 13 0 011.7 4h-3.4A13 13 0 0112 4zM4.3 14a8 8 0 010-4h2.9a16 16 0 000 4zm1.2 2h2.5a15 15 0 001.3 3.4A8 8 0 015.5 16zm2.5-8H5.5a8 8 0 013.2-3.4A15 15 0 007.4 8zM12 20a13 13 0 01-1.7-4h3.4A13 13 0 0112 20zm2.3-6H9.7a14 14 0 010-4h4.6a14 14 0 010 4zm.5 5.4a15 15 0 001.3-3.4h2.5a8 8 0 01-3.8 3.4zm1.7-5.4a16 16 0 000-4h2.9a8 8 0 010 4z'/>,
}

export type IconProps = {
    name: IconName
    className?: string
    size?: number
} & Omit<SVGProps<SVGSVGElement>, 'name'>

export function Icon({name, className = '', size = 16, ...rest}: IconProps): ReactElement {
    return (
        <svg
            className={`Icon ${className}`}
            width={size}
            height={size}
            viewBox='0 0 24 24'
            fill='currentColor'
            aria-hidden={true}
            {...rest}
        >
            {paths[name]}
        </svg>
    )
}

/** Upstream's property-type glyphs (webapp/src/properties/*). */
export const propertyIcons: Record<string, IconName> = {
    text: 'text',
    slug: 'text',
    number: 'number',
    select: 'color',
    multiSelect: 'color',
    date: 'date',
    person: 'user',
    multiPerson: 'users',
    checkbox: 'checkbox',
    url: 'link',
    email: 'message',
    phone: 'edit',
    file: 'attachment',
    createdTime: 'date',
    updatedTime: 'date',
    createdBy: 'user',
    updatedBy: 'user',
    unknown: 'help',
}

export const viewIcons: Record<string, IconName> = {
    board: 'view-board',
    table: 'view-table',
    gallery: 'view-gallery',
    calendar: 'view-calendar',
}
